import React from 'react';
import { Trophy, Award, Star, CheckCircle, Code, Server, Smartphone, Shield } from 'lucide-react';
import { Badge } from './ui/badge';

const Achievements = () => {
  const achievements = [
    {
      id: 1,
      icon: <Server className="w-8 h-8" />,
      title: "Odoo ERP Development",
      description: "Recently contributed on developing custom modules and integrations for Odoo ERP system, enhancing business process automation and workflow efficiency.",
      tags: ["Odoo", "ERP", "Python", "PostgreSQL"],
      color: "from-purple-500 to-purple-700"
    },
    {
      id: 2,
      icon: <Smartphone className="w-8 h-8" />,
      title: "Cross-Platform Mobile ERP Integration",
      description: "Built a comprehensive cross-platform mobile application for ERP integration using XML-RPC protocol. Implemented Redis caching for optimal performance, RESTful API architecture, token-based verification, and custom authorization/authentication system.",
      tags: ["React Native", "XML-RPC", "Redis", "REST API", "JWT",'zustand'],
      color: "from-blue-500 to-cyan-700",
      highlights: [
        "XML-RPC protocol integration",
        "Redis caching implementation",
        "Token verification system",
        "Custom authorization & authentication"
      ]
    },
    {
      id: 3,
      icon: <Code className="w-8 h-8" />,
      title: "Fuel Station Management System",
      description: "Developed a full-stack fuel station management application with web admin panel using React, Node.js, and MongoDB. Features include real-time inventory tracking, sales management, and comprehensive reporting.",
      tags: ["React", "Node.js", "MongoDB", "Express","Redux"],
      color: "from-green-500 to-emerald-700",
      highlights: [
        "Token-based authentication",
        "Custom authorization system",
        "RESTful API architecture",
        "Redis caching for performance",
        "Web admin panel"
      ]
    },
    {
      id: 4,
      icon: <CheckCircle className="w-8 h-8" />,
      title: "IT Ticketing System",
      description: "Created a comprehensive ticketing system for IT departments with user-based access control, priority management, ticket lifecycle tracking, and automated notifications.",
      tags: ["React", "Node.js", "MongoDB", "WebSocket"],
      color: "from-orange-500 to-red-700",
      highlights: [
        "User role-based access control",
        "Real-time ticket updates",
        "Priority & status management",
        "Email notifications",
        "Fully responsive web design"
      ]
    },
    {
      id: 5,
      icon: <Shield className="w-8 h-8" />,
      title: "Maintenance Management System",
      description: "Built a robust maintenance management system for maintenance departments featuring work order management, asset tracking, preventive maintenance scheduling, and user-based access control.",
      tags: ["React", "Node.js", "MongoDB", "JWT"],
      color: "from-indigo-500 to-purple-700",
      highlights: [
        "User-based access control",
        "Work order management",
        "Asset tracking system",
        "Preventive maintenance scheduling",
        "Fully responsive web application"
      ]
    },
    {
      id: 6,
      icon: <Star className="w-8 h-8" />,
      title: "E-Commerce Plant Selling Platform",
      description: "Developed a feature-rich e-commerce platform specialized in plant selling with complete modern e-commerce functionality including shopping cart, payment gateway integration, order management, and comprehensive admin panel.",
      tags: ["React", "Node.js", "MongoDB", "Stripe", "PayPal"],
      color: "from-pink-500 to-rose-700",
      highlights: [
        "Full e-commerce functionality",
        "Payment gateway integration",
        "Admin panel with analytics",
        "User-based access control",
        "Product management system",
        "Order tracking & management",
        "Responsive design"
      ]
    }
  ];

  const stats = [
    { number: "15+", label: "Projects Completed", icon: <Award className="w-6 h-6" /> },
    { number: "6", label: "Major Systems", icon: <Trophy className="w-6 h-6" /> },
    { number: "10+", label: "Technologies", icon: <Code className="w-6 h-6" /> },
    { number: "100%", label: "Client Satisfaction", icon: <Star className="w-6 h-6" /> }
  ];

  return (
    <section id="achievements" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-full mb-4">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Achievements & Projects
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-cyan-600 to-blue-600 mx-auto mb-8"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Delivering innovative solutions across multiple domains, from ERP systems to e-commerce platforms, 
            with a focus on scalability, security, and user experience.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 text-center"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 bg-cyan-100 rounded-full mb-3 text-cyan-600">
                {stat.icon}
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Achievements Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
            >
              {/* Card Header with Gradient */}
              <div className={`bg-gradient-to-r ${achievement.color} p-6 text-white`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 group-hover:scale-110 transition-transform">
                    {achievement.icon}
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-2">{achievement.title}</h3>
              </div>

              {/* Card Content */}
              <div className="p-6">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {achievement.description}
                </p>

                {/* Highlights */}
                {achievement.highlights && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                      Key Features:
                    </h4>
                    <ul className="space-y-2">
                      {achievement.highlights.map((highlight, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-600">
                          <span className="inline-block w-1.5 h-1.5 bg-cyan-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                  {achievement.tags.map((tag, index) => (
                    <Badge
                      key={index}
                      className="bg-gray-100 text-gray-700 hover:bg-cyan-100 hover:text-cyan-700 transition-colors"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Ready to Build Something Amazing?
            </h3>
            <p className="text-xl text-cyan-50 mb-6 max-w-2xl mx-auto">
              Let's collaborate on your next project and bring your ideas to life with cutting-edge technology and best practices.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center px-8 py-4 bg-white text-cyan-600 font-semibold rounded-full hover:bg-gray-100 transition-all hover:scale-105 shadow-lg"
            >
              Get In Touch
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Achievements;